import React, {
    ChangeEvent,
    forwardRef,
    FunctionComponent,
    HTMLProps,
    KeyboardEvent,
    SyntheticEvent,
    useEffect,
    useCallback,
    useRef,
    useState,
} from 'react';

import classnames from 'classnames';
import {
    cloneDeep,
    escapeRegExp,
    every,
    find,
    findIndex,
    forEach,
    includes,
    isEmpty,
    map,
    size,
    startsWith,
    trim,
    uniq,
} from 'lodash';
import { Button, Dropdown, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { trackPromise } from 'react-promise-tracker';

import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { ModuleId } from 'admin/modules/Modules';
import { isAuthorized, useCurrentContext, useProfile } from 'auth/Auth';
import { AdvancedFilter } from 'common/filters/filter-bar/AdvancedFilter/AdvancedFilter';
import { MultiSelectFilter } from 'common/filters/filter-bar/multi-select-filter/MultiSelectFilter';
import { SingleSelectFilter } from 'common/filters/filter-bar/single-select-filter/SingleSelectFilter';
import {
    cancelGetFilterValues,
    getFilterValues,
    isMultiSelect,
    saveAdvancedFilter,
    getAdvancedFilter,
    deleteAdvancedFilter,
} from 'common/filters/Filters';
import { FilterDefinition, FilterValue, AdvancedFilterRules, ObjectModel } from 'common/filters/Filters.types';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { Overlay } from 'core/overlay/Overlay';
import { KeyCode } from 'core/util/KeyCode';
import { Filter as SearchFilter } from 'core/util/Search';

import 'common/filters/filter-bar/filter-picker/FilterPicker.scss';

interface Props {
    filters: FilterDefinition[];
    selectedFilters?: SearchFilter[];
    moduleIDs?: ModuleId[];
    filterPickerText?: string;
    overlayArea?: string;
    onSelect: (filter: FilterDefinition) => void;
    reloadFilter?: (initial: boolean, reset: boolean, searchFilters: SearchFilter[], refresh: boolean) => void;
    showSelectAll?: boolean;
    isFilterPickerDisabled?: boolean;
}

const FILTER_DEPENDENCIES: Record<string, { parent: string; requiredValue: string }> = {
    'R4ETicket.ticket.surveyTemplate': {
        parent: 'R4ETicket.ticket.ticketType',
        requiredValue: 'Survey',
    },
    // Can Add other dependencies here as needed
};

export const FilterPicker: FunctionComponent<Props> = ({
    filters,
    selectedFilters,
    moduleIDs,
    onSelect,
    reloadFilter,
    filterPickerText,
    overlayArea,
    showSelectAll,
    isFilterPickerDisabled = false,
}) => {
    const currentContext = useCurrentContext();
    const { t } = useTranslation(['common/filters']);

    const [isOpen, setOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');
    const [filterValueSearch, setFilterValueSearch] = useState('');

    const [filtersByGroup, setFiltersByGroup] = useState<{ [group: string]: FilterDefinition[] }>({});
    const [selectedFilterDefinition, setSelectedFilterDefinition] = useState<FilterDefinition>();
    const [filterValuesStart, setFilterValuesStart] = useState(0);
    const [hasMoreFilterValues, setHasMoreFilterValues] = useState(true);
    const [showAdvancedFilter, setShowAdvancedFilter] = useState<{ show: boolean; filter?: FilterValue }>({
        show: false,
    });
    const [advancedFilterData, setAdvancedFilterData] = useState<AdvancedFilterRules | null>(null);

    const [{ profile }] = useProfile();
    const [, dispatchMessage] = useMessage();

    const FILTER_VALUES_COUNT = 250;

    const filterPickerClassNames = classnames('r4e-filter-picker', {
        open: isOpen,
    });

    const ref = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const multiSelectDraftRef = useRef<FilterValue[] | null>(null);

    const DROPDOWN_MENU_CLASS = 'dropdown-menu';
    // const DROPDOWN_ITEM_CLASS = 'dropdown-item';
    // const OPTION_FOCUS_CLASS = 'focus';
    const VIRTUALIZED_LIST_CLASS = 'ReactVirtualized__List';
    const PICKER_OPTION_SELECTOR = '[data-picker-option="true"]';
    const PICKER_OPTION_FOCUSED_ATTR = 'data-picker-option-focused';

    useEffect(() => {
        groupFilters();
    }, [filters, selectedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!showAdvancedFilter.show) {
            setAdvancedFilterData(null);
        }
    }, [showAdvancedFilter]);

    const handleMultiSelectDraftChange = (values: FilterValue[]): void => {
        multiSelectDraftRef.current = values;
    };

    const isFilterVisible = (filter: FilterDefinition): boolean => {
        // Hide already selected filters
        const isAlreadySelected = selectedFilters?.some((sf) => sf.name === filter.name);

        if (isAlreadySelected) {
            return false;
        }

        const dependency = FILTER_DEPENDENCIES[filter.name];

        if (!dependency) {
            return true; // No dependency = always visible
        }

        const parentFilter = selectedFilters?.find((sf) => sf.name === dependency.parent);

        if (!parentFilter) {
            return false; // Parent not selected = hide dependent
        }

        // This happens when user clicks outside without selecting specific values
        if (parentFilter.all || isEmpty(parentFilter.values)) {
            return true;
        }

        // Otherwise, check if the specific required value is selected
        const hasRequiredValue = parentFilter.values.some((value) => value === dependency.requiredValue);

        return hasRequiredValue;
    };

    const groupFilters = (): void => {
        // Build a fresh object every run instead of mutating the state reference.
        // The previous implementation mutated the existing `filtersByGroup` state in place
        // and then called setFiltersByGroup with the SAME reference, which made React's
        // bailout (Object.is) skip re-renders. When `filters` updated rapidly during page
        // load (e.g. an initial getFilterDefinitions response followed by a refreshFilters
        // pass), late-arriving filters such as Location and Source could be silently
        // dropped from the picker UI even though they were correctly present in `filters`.
        const nextFiltersByGroup: { [group: string]: FilterDefinition[] } = {};
        const uniqueGroups = uniq(map(filters, 'group'));

        uniqueGroups.forEach((group) => {
            nextFiltersByGroup[group] = [];
        });

        filters.forEach((filter) => {
            if (!filter.selected && isFilterVisible(filter)) {
                if (!nextFiltersByGroup[filter.group]) {
                    nextFiltersByGroup[filter.group] = [];
                }
                nextFiltersByGroup[filter.group].push(filter);
            }
        });

        setFiltersByGroup(nextFiltersByGroup);
    };

    const getAppliedMultiSelectFilter = (filterDefinition: FilterDefinition): FilterDefinition => {
        const draftValues = cloneDeep(multiSelectDraftRef.current ?? filterDefinition.values ?? []);
        const selectedValues = draftValues.filter((value) => value.selected);

        return {
            ...filterDefinition,
            selected: true,
            values: selectedValues,
        };
    };

    const handleOutsideClick = (event: Event): void => {
        if (!ref.current || ref.current.contains(event.target as Node)) {
            return;
        }

        const openChildFilterName = ref.current
            .querySelector('[data-filter-menu="true"]')
            ?.getAttribute('data-attr-name');

        if (openChildFilterName) {
            const filterDefinition = find(filters, (filter) => filter.name === openChildFilterName);

            if (filterDefinition && isMultiSelect(filterDefinition)) {
                closeDropdown(getAppliedMultiSelectFilter(filterDefinition));
                return;
            }

            if (filterDefinition) {
                closeDropdown(filterDefinition);
                return;
            }
        }

        closeDropdown();
    };

    // const handleOutsideClick = (event: Event): void => {
    //     if (ref.current && !ref.current.contains(event.target as Node)) {
    //         if (!selectedFilterDefinition) {
    //             const filterName = ref.current
    //                 .getElementsByClassName(DROPDOWN_MENU_CLASS)
    //                 .item(0)
    //                 ?.getAttribute('data-attr-name');
    //             const filterDefinition = find(filters, (filter) => filter.name === filterName && !filter.selected);

    //             if (filterDefinition && isMultiSelect(filterDefinition)) {
    //                 if (filterDefinition.values && filterDefinition.values.length > 0) {
    //                     filterDefinition.selected = true;
    //                     setSelectedFilterDefinition(filterDefinition);
    //                     closeDropdown(filterDefinition);
    //                 } else {
    //                     closeDropdown();
    //                 }
    //             } else {
    //                 closeDropdown();
    //             }
    //         } else {
    //             const filterToApply =
    //                 selectedFilterDefinition && isMultiSelect(selectedFilterDefinition)
    //                     ? {
    //                           ...selectedFilterDefinition,
    //                           values: multiSelectDraftRef.current ?? selectedFilterDefinition.values,
    //                       }
    //                     : selectedFilterDefinition;

    //             closeDropdown(filterToApply);
    //             // closeDropdown(selectedFilterDefinition);
    //         }
    //     }
    // };

    const openDropdown = (): void => {
        setOpen(true);
        document.addEventListener('mousedown', handleOutsideClick);
    };

    const closeDropdown = (filterDefinition?: FilterDefinition): void => {
        let timeout = 0;

        if (filterDefinition) {
            timeout = 300;
            cancelGetFilterValues();
            onSelect(cloneDeep(filterDefinition));
        }

        setTimeout(() => {
            reset();
        }, timeout);

        setOpen(false);
        document.removeEventListener('mousedown', handleOutsideClick);
    };

    const toggle = (open: boolean, event: SyntheticEvent<Dropdown>, metadata?: any): void => {
        if (open) {
            openDropdown();
        } else if (
            event.currentTarget instanceof HTMLButtonElement &&
            includes(event.currentTarget.className, 'dropdown-toggle')
        ) {
            if (!filterSearch.length) {
                setTimeout(() => {
                    closeDropdown(selectedFilterDefinition);
                }, 10);
            } else {
                reset();
            }
        }
    };

    const reset = (): void => {
        if (selectedFilterDefinition) {
            selectedFilterDefinition.values.forEach((filterValue) => {
                // eslint-disable-next-line no-param-reassign
                filterValue.selected = false;
            });

            filters.forEach((filter) => {
                if (filter.name === selectedFilterDefinition.name) {
                    // eslint-disable-next-line no-param-reassign
                    filter.selected = false;
                }
            });
        }

        setSelectedFilterDefinition(undefined);
        setFilterSearch('');
        multiSelectDraftRef.current = null;
        setFilterValueSearch('');
    };

    const getButtonVariant = (): string => {
        return isOpen ? 'icon' : 'link';
    };

    const getButtonIcon = (): string => {
        return isOpen ? 'icon-times' : 'icon-plus';
    };

    const filterFilters = (filterDefinition: FilterDefinition): boolean => {
        if (filterDefinition.selected) {
            return false;
        }

        const search = trim(filterSearch);
        let isMatch = true;

        if (!isEmpty(search)) {
            const pattern = new RegExp(escapeRegExp(search), 'i');

            isMatch = pattern.test(filterDefinition.label) || pattern.test(filterDefinition.name);
        }

        return isMatch;
    };

    const searchFilterValue = (filter: FilterValue): boolean => {
        const search = trim(filterSearch);
        let isMatch = true;

        if (!isEmpty(search)) {
            const pattern = new RegExp(escapeRegExp(search), 'i');

            isMatch = pattern.test(filter.label);
        }

        return isMatch;
    };

    const getValues = async (filterDefinition: FilterDefinition, start: number): Promise<void> => {
        const request = {
            name: filterDefinition.name,
            objectFieldName: filterDefinition.objectFieldName,
            moduleIDs,
            selectedFilters,
            start,
            count: FILTER_VALUES_COUNT,
        };
        const args: any = {};
        const index = findIndex(filters, (filter) => filter.name === filterDefinition.name);

        try {
            if (filterDefinition.name === 'R4EEntity.entityMetadata.location') {
                const location = Array.from(profile?.user?.scopes || []).find((e) => e.name === 'location');

                if (location && location.values && location.values.length >= 0) {
                    args.includeValues = location.values;
                }
            }
            const filterValues = await trackPromise(
                getFilterValues(
                    currentContext!.agencyId,
                    currentContext!.tenantId,
                    filterDefinition.name,
                    request,
                    args,
                ),
                'menu',
            );

            /* eslint-disable no-param-reassign */
            if (request.start === 0) {
                filters[index].values = [];
                filters[index].valuesCount = filterValues.count;
                filterDefinition.valuesCount = filterValues.count;
            }

            if (filterValues.values) {
                filters[index].values.splice(start, FILTER_VALUES_COUNT, ...filterValues.values);
                filterDefinition.values = filters[index].values;
            }
            /* eslint-enable no-param-reassign */

            setSelectedFilterDefinition(filterDefinition);
            multiSelectDraftRef.current = cloneDeep(filterDefinition.values || []);
            setFilterValuesStart(start);

            if (size(filterValues.values) < FILTER_VALUES_COUNT) {
                setTimeout(() => {
                    setHasMoreFilterValues(false);
                }, 10);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const selectFilter = (filterDefinition: FilterDefinition, selectedFilter?: FilterValue): void => {
        multiSelectDraftRef.current = null;
        const filterDefn = { ...filterDefinition };

        filterDefn.selected = true;
        filterDefn.values = [];
        if (filterDefinition.name === ObjectModel.AdvancedKeywordFilter && selectedFilter) {
            filterDefinition.values.forEach((value) => {
                // eslint-disable-next-line no-param-reassign
                value.selected = value.value === selectedFilter.value;
            });
            closeDropdown(filterDefinition);
        } else {
            getValues(filterDefn, 0);
        }

        // setFilterSearch(`${filterDefn.label}: `);

        const nextSearchValue = `${filterDefn.label}: `;

        setFilterSearch(nextSearchValue);
        setFilterValueSearch('');
        focusSearchAtEnd(nextSearchValue);
    };

    const adjustScroll = (container: Element, items: ArrayLike<Element>, index: number): void => {
        const element = items[index];
        const rect = element.getBoundingClientRect();
        const elementInView = rect.top - rect.height >= container.scrollTop && rect.top <= container.clientHeight;

        /* eslint-disable no-param-reassign */
        if (index === 0) {
            container.scrollTop = 0;
        } else if (index === items.length - 1) {
            container.scrollTop = container.scrollHeight;
        } else if (!elementInView) {
            element.scrollIntoView();

            if (selectedFilterDefinition) {
                if (isMultiSelect(selectedFilterDefinition)) {
                    // Has fixed header
                    container.scrollTop -= container.querySelector('.dropdown-header')!.clientHeight;
                } else if (index < 3) {
                    // Hack to fix single select scroll
                    container.scrollTop = 0;
                }
            }
        }
        /* eslint-enable no-param-reassign */
    };

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const oldValue = filterSearch;
        let newValue = event.target.value;

        if (
            (size(oldValue) && !size(newValue)) ||
            (selectedFilterDefinition && !startsWith(newValue, selectedFilterDefinition.label))
        ) {
            // Handle deletion of entire query || values query
            setSelectedFilterDefinition(undefined);
        }

        setFilterSearch(newValue);
        if (selectedFilterDefinition) {
            newValue = trim(newValue.substring(selectedFilterDefinition.label.length + 1));
            setFilterValueSearch(newValue);
        }
    };

    const handleKeyPress = (event: KeyboardEvent): void => {
        switch (event.keyCode) {
            case KeyCode.Space: {
                const options = getPickerOptions();
                const focusedIndex = getFocusedPickerOptionIndex(options);
                const focusedItem = focusedIndex >= 0 ? options[focusedIndex] : null;

                if (focusedItem) {
                    event.preventDefault();
                    focusedItem.click();
                }

                break;
            }
            case KeyCode.Esc: {
                event.stopPropagation();
                // Close dropdown or clear input
                if (isEmpty(filterSearch)) {
                    closeDropdown();
                } else {
                    reset();
                }

                break;
            }

            case KeyCode.ArrowDown: {
                if (selectedFilterDefinition && event.target === searchInputRef.current) {
                    event.preventDefault();
                    event.stopPropagation();

                    const firstChildFocusable = getFirstChildFocusable();

                    firstChildFocusable?.focus();
                    break;
                }

                // LEVEL 1: move from parent search into first-level options
                if (!selectedFilterDefinition && event.target === searchInputRef.current) {
                    const dropdown = ref.current?.querySelector(`.${DROPDOWN_MENU_CLASS}`);
                    const options = getPickerOptions();

                    if (dropdown && options.length) {
                        event.preventDefault();
                        event.stopPropagation();

                        setFocusedPickerOption(options, 0);
                        adjustScroll(dropdown, options, 0);
                    }

                    break;
                }

                const dropdown = ref.current?.querySelector(`.${DROPDOWN_MENU_CLASS}`);
                const options = getPickerOptions();

                if (dropdown && options.length) {
                    event.preventDefault();

                    const focusedIndex = getFocusedPickerOptionIndex(options);
                    const nextIndex = focusedIndex >= 0 && focusedIndex + 1 < options.length ? focusedIndex + 1 : 0;

                    setFocusedPickerOption(options, nextIndex);
                    adjustScroll(dropdown, options, nextIndex);
                }
                break;
            }

            case KeyCode.ArrowUp: {
                const dropdown = ref.current?.querySelector(`.${DROPDOWN_MENU_CLASS}`);
                const options = getPickerOptions();

                if (!dropdown || !options.length) {
                    break;
                }

                // If focus is currently in the search input, always enter the list
                // from the last visible option.
                if (!selectedFilterDefinition && event.target === searchInputRef.current) {
                    event.preventDefault();
                    event.stopPropagation();

                    const lastIndex = options.length - 1;

                    setFocusedPickerOption(options, lastIndex);
                    adjustScroll(dropdown, options, lastIndex);
                    return;
                }

                event.preventDefault();

                const focusedIndex = getFocusedPickerOptionIndex(options);
                const nextIndex = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;

                setFocusedPickerOption(options, nextIndex);
                adjustScroll(dropdown, options, nextIndex);
                break;
            }

            case KeyCode.Tab: {
                const options = getPickerOptions();
                const focusedIndex = getFocusedPickerOptionIndex(options);
                const focusedItem = focusedIndex >= 0 ? options[focusedIndex] : null;

                if (focusedItem) {
                    event.preventDefault();
                    focusedItem.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                }

                break;
            }

            case KeyCode.Enter: {
                if (selectedFilterDefinition) {
                    const filterToApply = isMultiSelect(selectedFilterDefinition)
                        ? {
                              ...selectedFilterDefinition,
                              values: multiSelectDraftRef.current ?? selectedFilterDefinition.values,
                          }
                        : selectedFilterDefinition;

                    closeDropdown(filterToApply);
                    break;
                }

                const options = getPickerOptions();
                const focusedIndex = getFocusedPickerOptionIndex(options);
                const focusedItem = focusedIndex >= 0 ? options[focusedIndex] : null;

                if (focusedItem) {
                    event.preventDefault();
                    focusedItem.click();
                }

                break;
            }

            case KeyCode.Semicolon: {
                // If filterSearch matches filter name, select filter and render filter values
                const filterName = trim(filterSearch, ': ');

                if (event.shiftKey && !isEmpty(filterName) && !selectedFilterDefinition) {
                    const pattern = new RegExp(`^${escapeRegExp(filterName)}$`, 'i');
                    const match = find(filters, (filter) => {
                        return !filter.selected && pattern.test(filter.label);
                    });

                    if (match) {
                        event.preventDefault();
                        selectFilter(match);
                    }
                }

                break;
            }

            default:
                break;
        }
    };

    const clearFocusedPickerOption = (options: HTMLElement[]): void => {
        options.forEach((item) => {
            const element = item;

            element.setAttribute(PICKER_OPTION_FOCUSED_ATTR, 'false');
            element.tabIndex = -1;
        });
    };

    const getChildMenu = (): HTMLElement | null => {
        return ref.current?.querySelector('[data-filter-menu="true"]') as HTMLElement | null;
    };

    const getFirstChildFocusable = (): HTMLElement | null => {
        const menu = getChildMenu();

        if (!menu) return null;

        return (
            menu.querySelector<HTMLElement>('[data-child-search="true"]') ||
            menu.querySelector<HTMLElement>('[data-child-bulk-all="true"]') ||
            menu.querySelector<HTMLElement>('[data-child-bulk-none="true"]') ||
            menu.querySelector<HTMLElement>('[data-child-option="true"]')
        );
    };

    const onChange = (filterDefinition: FilterDefinition): void => {
        closeDropdown(filterDefinition);
    };

    const getPickerOptions = (): HTMLElement[] => {
        if (!ref.current || selectedFilterDefinition) {
            return [];
        }

        return Array.from(ref.current.querySelectorAll<HTMLElement>(PICKER_OPTION_SELECTOR)).filter(
            (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
        );
    };

    const getFocusedPickerOptionIndex = (options: HTMLElement[]): number => {
        return options.findIndex((item) => item.getAttribute(PICKER_OPTION_FOCUSED_ATTR) === 'true');
    };

    const setFocusedPickerOption = (options: HTMLElement[], nextIndex: number): void => {
        options.forEach((item, index) => {
            const isFocused = index === nextIndex;
            const element = item;

            element.setAttribute(PICKER_OPTION_FOCUSED_ATTR, isFocused ? 'true' : 'false');
            element.tabIndex = isFocused ? 0 : -1;
        });

        options[nextIndex]?.focus();
    };

    const getVisiblePickerOptionCount = (): number => {
        if (selectedFilterDefinition) {
            const visibleValues = (selectedFilterDefinition.values || []).filter((filterValue: FilterValue) => {
                const search = trim(filterValueSearch);

                let isMatch = true;

                if (!isEmpty(search)) {
                    const pattern = new RegExp(escapeRegExp(search), 'i');

                    isMatch = pattern.test(filterValue.label);
                }

                return isMatch;
            });

            const count = visibleValues.length;

            return count;
        }
        let count = 0;

        forEach(filtersByGroup, (groupedFilters: FilterDefinition[], group: string) => {
            if (!isEmpty(groupedFilters)) {
                const enableKeywordSearch = isAuthorized({
                    featureFlags: [FeatureFlagId.EnableKeywordSearch],
                });

                const allowAdvancedSearch =
                    !isEmpty(groupedFilters) &&
                    group === 'AdvancedFilter' &&
                    groupedFilters[0].values &&
                    enableKeywordSearch;

                if (group !== 'AdvancedFilter') {
                    count += groupedFilters.filter((filter: FilterDefinition) => filterFilters(filter)).length;
                } else if (allowAdvancedSearch) {
                    count += groupedFilters[0].values.filter((filter: FilterValue) => searchFilterValue(filter)).length;
                }
            }
        });

        if (
            isAuthorized({
                featureFlags: [FeatureFlagId.EnableKeywordSearch],
            })
        ) {
            count += 1; // Add advanced filter action
        }

        return count;
    };

    type ButtonProps = HTMLProps<HTMLButtonElement>;
    type DivProps = HTMLProps<HTMLDivElement>;

    const FilterPickerToggle = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
        <Button
            ref={ref}
            className={props.className}
            aria-label={isOpen ? 'Filter picker expanded' : 'Add filter'}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls="filter-picker-menu"
            disabled={every(filtersByGroup, (groupedFilters) => isEmpty(groupedFilters)) || isFilterPickerDisabled}
            variant={getButtonVariant()}
            onClick={(e: any): void => {
                toggle(!isOpen, e);
            }}
        >
            {props.children}
        </Button>
    ));

    const FilterPickerMenu = forwardRef<HTMLDivElement, DivProps>((props, ref) => {
        const items: JSX.Element[] = [];
        const enableKeywordSearch = isAuthorized({
            featureFlags: [FeatureFlagId.EnableKeywordSearch],
        });
        let groupIndex = 0;

        forEach(filtersByGroup, (groupedFilters: FilterDefinition[], group: string) => {
            if (!isEmpty(groupedFilters)) {
                const allowAdvancedSearch =
                    !isEmpty(groupedFilters) &&
                    group === 'AdvancedFilter' &&
                    groupedFilters[0].values &&
                    enableKeywordSearch;

                if (group !== 'AdvancedFilter') {
                    if (groupIndex > 0) {
                        items.push(<Dropdown.Divider key={`divider-${groupIndex}`} />);
                    }
                    items.push(<Dropdown.Header key={group}>{group}</Dropdown.Header>);

                    groupedFilters
                        .filter((filter: FilterDefinition) => filterFilters(filter))
                        .forEach((filter: FilterDefinition, filterIndex: number) => {
                            items.push(
                                <Dropdown.Item
                                    data-picker-option="true"
                                    data-picker-option-focused={
                                        groupIndex === 0 && filterIndex === 0 ? 'true' : 'false'
                                    }
                                    key={filter.name}
                                    onClick={(): void => selectFilter(filter)}
                                    role="radio"
                                    aria-checked={filter.selected}
                                >
                                    {filter.label}
                                </Dropdown.Item>,
                            );
                        });
                } else if (allowAdvancedSearch) {
                    if (groupedFilters[0].values?.length) {
                        if (groupIndex > 0) {
                            items.push(<Dropdown.Divider key={`divider-${groupIndex}`} />);
                        }
                        items.push(<Dropdown.Header key={group}>{t('ADVANCED_FILTER')}</Dropdown.Header>);
                    }

                    groupedFilters[0].values
                        .filter((filter: FilterValue) => searchFilterValue(filter))
                        .forEach((filter: FilterValue) => {
                            items.push(
                                <Dropdown.Item
                                    key={filter.label}
                                    className="advanced-filter-item"
                                    data-picker-option="true"
                                    data-picker-option-focused="false"
                                    tabIndex={-1}
                                    role="radio"
                                    aria-checked={filter.selected}
                                >
                                    <Button
                                        onClick={(): void => selectFilter(groupedFilters[0], filter)}
                                        variant="light"
                                        className="filter-label"
                                    >
                                        {filter.label}
                                    </Button>
                                    <Button
                                        variant="link"
                                        onClick={(): void => {
                                            getAdvancedFilterValues(filter);
                                        }}
                                        className="filter-label-button"
                                    >
                                        {t('EDIT')}
                                    </Button>
                                </Dropdown.Item>,
                            );
                        });
                    items.push(<Dropdown.Divider key="divider-addKeywordFilter" />);
                }
                groupIndex += 1;
            }
        });

        if (enableKeywordSearch) {
            items.push(
                <Dropdown.Item
                    data-picker-option="true"
                    data-picker-option-focused="false"
                    tabIndex={-1}
                    onClick={() => {
                        closeDropdown();
                        setShowAdvancedFilter({ show: true });
                    }}
                >
                    <Button className="add-filter-button" variant="link">
                        {t('ADD_ADVANCED_FILTER')}
                    </Button>
                </Dropdown.Item>,
            );
        }

        return (
            <Dropdown.Menu role="radiogroup" aria-label="Available filters" ref={ref} onKeyDown={handleKeyPress}>
                {props.children}
                {items}
            </Dropdown.Menu>
        );
    });

    const showLoader = (): boolean => {
        const virtualizedList = ref.current?.querySelector(`.${VIRTUALIZED_LIST_CLASS}`);

        if (virtualizedList) {
            // If list is scrolled to bottom
            return Math.ceil(virtualizedList.scrollHeight - virtualizedList.scrollTop) === virtualizedList.clientHeight;
        }

        return false;
    };

    const handleDelete = async (advancedFilterId: string) => {
        try {
            setShowAdvancedFilter({ show: false });
            await trackPromise(deleteAdvancedFilter(currentContext!.tenantId, advancedFilterId), overlayArea);
            // wrap into timeout needed to avoid hidden success notification on re-render
            setTimeout(
                () =>
                    // Show toast only after all operations are complete
                    dispatchMessage({
                        type: MessageActionType.ShowSuccess,
                        text: t('ADVANCED_FILTER_DELETE_SUCCESS', { filterName: showAdvancedFilter.filter?.label }),
                    }),
                2500,
            );

            if (reloadFilter) reloadFilter(false, false, [], true);
        } catch (e) {
            dispatchMessage({
                type: MessageActionType.ShowError,
                text: t('ADVANCED_FILTER_DELETE_FAILED'),
            });
        }
    };

    const handleSave = async (filterData: any) => {
        try {
            setShowAdvancedFilter({ show: false });
            await trackPromise(saveAdvancedFilter(currentContext!.tenantId, filterData), overlayArea);
            setTimeout(
                () =>
                    // Show toast only after all operations are complete
                    dispatchMessage({
                        type: MessageActionType.ShowSuccess,
                        text: t('ADVANCED_FILTER_SAVE_SUCCESS'),
                    }),
                2500,
            );
            if (reloadFilter) reloadFilter(false, false, [], true);
        } catch (e) {
            dispatchMessage({
                type: MessageActionType.ShowError,
                text: t('ADVANCED_FILTER_SAVE_FAILED'),
            });
        }
    };

    const getAdvancedFilterValues = async (filter: FilterValue) => {
        closeDropdown();
        try {
            const advancedFilterDataResponse = await trackPromise(
                getAdvancedFilter(currentContext!.tenantId, filter.value),
                overlayArea,
            );

            setAdvancedFilterData(advancedFilterDataResponse);
            setShowAdvancedFilter({ show: true, filter });
        } catch (e) {
            dispatchMessage({
                type: MessageActionType.ShowError,
                text: t('ADVANCED_FILTER_FETCH_FAILED'),
            });
        }
    };
    const focusSearchAtEnd = useCallback((value?: string): void => {
        window.requestAnimationFrame(() => {
            const input = searchInputRef.current;

            if (!input) return;

            input.focus();

            const pos = (value ?? input.value).length;

            input.setSelectionRange(pos, pos);
        });
    }, []);

    const renderDropdownMenu = () => {
        if (!selectedFilterDefinition) {
            return (
                <Dropdown.Menu as={FilterPickerMenu}>
                    <Overlay area="menu" />
                </Dropdown.Menu>
            );
        }

        if (
            isMultiSelect(selectedFilterDefinition) &&
            selectedFilterDefinition.name !== ObjectModel.AdvancedKeywordFilter
        ) {
            return (
                <Dropdown.Menu
                    filter={selectedFilterDefinition}
                    searchValue={filterValueSearch}
                    as={MultiSelectFilter}
                    showSelectAll={showSelectAll}
                    onKeyDown={handleKeyPress}
                    onDraftChange={handleMultiSelectDraftChange}
                >
                    <InfiniteScroll
                        pageStart={0}
                        initialLoad={false}
                        loadMore={(): void => {
                            if (!hasMoreFilterValues) return;

                            getValues(selectedFilterDefinition, filterValuesStart + FILTER_VALUES_COUNT);
                        }}
                        getScrollParent={(): HTMLElement | null =>
                            ref.current?.querySelector(`.${VIRTUALIZED_LIST_CLASS}`) || null
                        }
                        loader={
                            <i
                                className={`icon-spinner loader ${showLoader() ? '' : 'hidden'}`}
                                key={`${selectedFilterDefinition.name}-loader`}
                            />
                        }
                        hasMore={hasMoreFilterValues}
                        useWindow={false}
                    >
                        <></>
                    </InfiniteScroll>
                </Dropdown.Menu>
            );
        }

        return (
            <Dropdown.Menu filter={selectedFilterDefinition} onChange={onChange} as={SingleSelectFilter}>
                <Overlay area="menu" />
            </Dropdown.Menu>
        );
    };

    useEffect(() => {
        if (!isOpen) return undefined;

        const id = window.setTimeout(() => {
            searchInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(id);
    }, [isOpen, selectedFilterDefinition]);

    const visiblePickerOptionCount = getVisiblePickerOptionCount();

    return (
        <div className={filterPickerClassNames} ref={ref}>
            <div className="search-wrapper">
                <i className="icon-search" />
                <FormControl
                    ref={searchInputRef}
                    value={filterSearch}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyPress}
                    onFocus={() => {
                        const options = getPickerOptions();

                        clearFocusedPickerOption(options);
                    }}
                    aria-label={`Search filters. ${visiblePickerOptionCount} options available. Use arrow keys to move through the list.`}
                />
            </div>
            <Dropdown show={isOpen} onToggle={toggle} alignRight>
                <Dropdown.Toggle as={FilterPickerToggle}>
                    <span className="filter-picker-text">
                        <i className={getButtonIcon()} />
                        {!isOpen && <span>{filterPickerText}</span>}
                    </span>
                </Dropdown.Toggle>
                {renderDropdownMenu()}
            </Dropdown>
            {showAdvancedFilter.show && (
                <AdvancedFilter
                    show={showAdvancedFilter.show}
                    onHide={() => setShowAdvancedFilter({ show: false })}
                    onDelete={handleDelete}
                    onSave={handleSave}
                    initialRulesData={advancedFilterData}
                    initialShortName={showAdvancedFilter.filter?.label}
                />
            )}
        </div>
    );
    /* eslint-enable no-nested-ternary */
};
